import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Api, downloadFile, urlWithSignerAccessCode } from "../../../api/Assinafy"
import { getSignedVerificationMethodForNavigate } from "../../../utils/verificationMethodFromDocument"
import { readApiErrorMessage } from "../../../utils/signerSignature"
import {
  listSignerCertificates,
  loadWebPki,
  signWithCertificate,
  WEBPKI_INSTALL_URL,
} from "../../../utils/webPki"
import { Alert, Button, Icon, Input, Spinner } from "../../../components"
import { SignNavbar } from "../../../layout/Sign/Sign"
import ModalDeny from "./ModalDeny"
import "../styles.scss"
import "./certificate-sign.scss"

const TERMS_URL = "https://www.assinafy.com.br/termos-de-uso"

/** Data de validade (Date do Web PKI) no formato AAAA.MM.DD. */
const formatValidity = (value) => {
  if (!value) return ""
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ""
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, "0")
  const dd = String(date.getDate()).padStart(2, "0")
  return `${yyyy}.${mm}.${dd}`
}

/** Tela "Assinatura digital" (Certificado A1) com a seleção de certificados inline. */
const CertificateSignPage = () => {
  const navigate = useNavigate()
  const { signerAccessCode } = useParams()
  const location = useLocation()
  const { state } = location

  const [signer, setSigner] = useState(state?.signer || {})
  const [signDocument, setSignDocument] = useState(state?.document || null)
  const [modalCancel, setModalCancel] = useState()

  const [certs, setCerts] = useState([])
  const [selectedThumbprint, setSelectedThumbprint] = useState("")
  const [expanded, setExpanded] = useState({})
  const [listing, setListing] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [listError, setListError] = useState("")
  const [pageError, setPageError] = useState("")
  const [notInstalled, setNotInstalled] = useState(false)
  const [termsChecked, setTermsChecked] = useState(false)

  // Certificado A1 também exige o aceite dos termos de uso antes de assinar
  // (a API recusa a assinatura sem isso). Só mostra o checkbox se ainda não aceitou.
  const needsTerms = signer?.has_accepted_terms === false

  // CPF/CNPJ exigido para o signatário (filtra os certificados e faz o fail-fast).
  const requiredCpf = signer?.government_id || signDocument?.current_signer?.government_id || ""
  const pkiRef = useRef(null)
  const autoFetchedRef = useRef(false)

  const fetchCertificates = async ({ refresh = false, cpf } = {}) => {
    setListError("")
    if (refresh) {
      setRefreshing(true)
    } else {
      setListing(true)
    }

    try {
      const pki = pkiRef.current || (await loadWebPki())
      pkiRef.current = pki
      setNotInstalled(false)

      const wantCpf = cpf !== undefined ? cpf : requiredCpf
      const list = await listSignerCertificates(pki, wantCpf)
      setCerts(list)

      // Pré-seleciona o primeiro certificado, como no PoC de referência.
      setSelectedThumbprint((prev) => {
        if (prev && list.some((c) => c.thumbprint === prev)) return prev
        return list[0]?.thumbprint || ""
      })
    } catch (err) {
      if (err?.code === "not_installed") {
        setNotInstalled(true)
        setListError(
          "A extensão Web PKI não está instalada ou está desatualizada. Instale a extensão e atualize esta página.",
        )
      } else {
        setListError(err?.message || "Não foi possível listar os certificados. Tente novamente.")
      }
    } finally {
      setListing(false)
      setRefreshing(false)
      setHasFetched(true)
    }
  }

  useEffect(() => {
    if (!state?.verified || !state?.dataConfirmed) {
      navigate(`/sign/${signerAccessCode}`, { replace: true })
      return undefined
    }

    let cancelled = false

    // Garante dados do signatário (nome/CPF) e já dispara a listagem automática
    // dos certificados — o CPF vem do signer/self para filtrar corretamente.
    Api("signer/self")(signerAccessCode)
      .then(({ status, data: { data } }) => {
        if (cancelled) return
        const selfData = status === 200 ? data : {}
        if (status === 200) setSigner((prev) => ({ ...prev, ...data }))
        if (!autoFetchedRef.current) {
          autoFetchedRef.current = true
          fetchCertificates({
            cpf: selfData.government_id || signDocument?.current_signer?.government_id || "",
          })
        }
      })
      .catch(() => {
        if (cancelled) return
        if (!autoFetchedRef.current) {
          autoFetchedRef.current = true
          fetchCertificates({})
        }
      })

    if (!state?.document) {
      Api("signer/viewDocument")(signerAccessCode)
        .then(({ status, data: { data } }) => {
          if (!cancelled && status === 200) setSignDocument(data)
        })
        .catch(() => {})
    }

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signerAccessCode])

  // Uma extensão recém-instalada só injeta seu content script após um reload da
  // página (comportamento do Chrome). Enquanto estiver "não instalada", re-sondar
  // na mesma página nunca detecta — então o "Atualizar" recarrega a página.
  const handleRefresh = () => {
    if (notInstalled) {
      window.location.reload()
      return
    }
    fetchCertificates({ refresh: true })
  }

  const toggleDetails = (thumbprint) => {
    setExpanded((prev) => ({ ...prev, [thumbprint]: !prev[thumbprint] }))
  }

  const handleContinue = async () => {
    if (!selectedThumbprint) return
    if (needsTerms && !termsChecked) return
    setSubmitting(true)
    setListError("")

    try {
      // Aceita os termos de uso antes de assinar (A1 exige o aceite).
      if (needsTerms) {
        await Api("signer/acceptTerms")(signerAccessCode)
        setSigner((prev) => ({ ...prev, has_accepted_terms: true }))
      }

      const signerName = await signWithCertificate({
        pki: pkiRef.current,
        accessCode: signerAccessCode,
        thumbprint: selectedThumbprint,
        requiredCpf,
        certs,
      })

      navigate(`/sign/${signerAccessCode}/success`, {
        state: {
          documentName: signDocument?.name || signerName,
          verification_method: getSignedVerificationMethodForNavigate(signDocument, signer, state),
        },
        replace: true,
      })
    } catch (err) {
      const apiMessage = await readApiErrorMessage(err)
      setListError(
        err?.code === "cpf_mismatch"
          ? err.message
          : apiMessage || err?.message || "Não foi possível concluir a assinatura. Tente novamente.",
      )
      setSubmitting(false)
    }
  }

  const navbarItems = () => {
    const itemsMenu = []

    if (signDocument?.artifacts?.original) {
      itemsMenu.push({
        children: (
          <>
            <Icon id="icon-arrow_downward" className="me-1" size="20" />
            Baixar documento
          </>
        ),
        onClick: () => downloadFile(urlWithSignerAccessCode(signDocument.artifacts.original, signerAccessCode)),
      })
    }

    itemsMenu.push({
      children: (
        <span className="text-danger">
          <Icon id="icon-close" className="me-1" size="20" />
          Negar assinatura
        </span>
      ),
      onClick: () =>
        setModalCancel({
          description:
            "Ao recusar este documento todos os participantes serão informados e ninguém mais poderá assinar.",
        }),
    })

    return itemsMenu
  }

  const busy = listing || refreshing
  // Só mostra o "nenhum certificado" após a primeira listagem terminar — evita
  // o alerta piscar na janela entre o mount e o início do fetch (assíncrono).
  const isEmpty = hasFetched && !busy && !listError && certs.length === 0

  return (
    <>
      <SignNavbar items={navbarItems()} setModalCancel={setModalCancel} />

      <div className="certificate-sign">
        <div className="certificate-sign-content">
          {pageError && (
            <Alert show variant="warning" onClose={() => setPageError("")}>
              {pageError}
            </Alert>
          )}

          <div className="certificate-sign-illustration">
            <Icon id="icon-verified_user" size="44" />
          </div>

          <h1 className="certificate-sign-title">Assinatura digital</h1>
          <p className="certificate-sign-subtitle">
            Selecione o certificado que deseja usar para assinar
          </p>

          <div className="certificate-sign-panel">
            <div className="certificate-sign-panel-toolbar">
              <span className="certificate-sign-panel-hint">
                Escolha a ID digital que você deseja usar para assinar:
              </span>
              <Button
                variant="border"
                size="sm"
                onClick={handleRefresh}
                disabled={busy || submitting}
              >
                <Icon id="icon-cached" className="me-1" size="16" />
                Atualizar
              </Button>
            </div>

            {listError && (
              <Alert show variant="warning">
                {listError}
              </Alert>
            )}

            {notInstalled && (
              <a
                className="certificate-sign-install"
                href={WEBPKI_INSTALL_URL}
                target="_blank"
                rel="noreferrer"
              >
                Instalar extensão Web PKI
                <Icon id="icon-external" className="ms-1" size="12" />
              </a>
            )}

            {(busy || !hasFetched) && !listError && (
              <div className="certificate-sign-loading">
                <Spinner />
                <span>Carregando certificados…</span>
              </div>
            )}

            {isEmpty && (
              <div className="certificate-sign-empty" role="alert">
                <span className="certificate-sign-empty-icon">
                  <Icon id="icon-error" size="22" />
                </span>
                <span className="certificate-sign-empty-body">
                  <strong className="certificate-sign-empty-title">
                    Nenhum certificado encontrado nesta máquina
                  </strong>
                  <span className="certificate-sign-empty-text">
                    Não localizamos um certificado digital para o CPF/CNPJ exigido
                    {requiredCpf ? ` (${requiredCpf})` : ""}. Instale o certificado do titular
                    correto no computador e clique em <strong>Atualizar</strong>.
                  </span>
                </span>
              </div>
            )}

            {!busy &&
              certs.map((cert) => {
                const selected = cert.thumbprint === selectedThumbprint
                return (
                  <label
                    key={cert.thumbprint}
                    className={`cert-row${selected ? " cert-row-selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="certificate"
                      className="cert-row-radio"
                      checked={selected}
                      onChange={() => setSelectedThumbprint(cert.thumbprint)}
                      disabled={submitting}
                    />
                    <span className="cert-row-icon">
                      <Icon id="icon-verified_user" size="20" />
                    </span>
                    <span className="cert-row-body">
                      <span className="cert-row-name">
                        {cert.subjectName || cert.pkiBrazil?.responsavel || "Certificado"}
                        <Button
                          variant="link"
                          className="cert-row-details-toggle"
                          onClick={(event) => {
                            event.preventDefault()
                            toggleDetails(cert.thumbprint)
                          }}
                        >
                          ver detalhes
                        </Button>
                      </span>
                      <span className="cert-row-meta">
                        Emitido por: {cert.issuerName || "—"}, Expira em: {formatValidity(cert.validityEnd) || "—"}
                      </span>
                      {expanded[cert.thumbprint] && (
                        <span className="cert-row-extra">
                          {cert.pkiBrazil?.cpf && <span>CPF: {cert.pkiBrazil.cpf}</span>}
                          {cert.pkiBrazil?.cnpj && <span>CNPJ: {cert.pkiBrazil.cnpj}</span>}
                          {cert.email && <span>E-mail: {cert.email}</span>}
                          {cert.subjectDN && <span className="cert-row-dn">{cert.subjectDN}</span>}
                        </span>
                      )}
                    </span>
                  </label>
                )
              })}

            {needsTerms && !busy && certs.length > 0 && (
              <label className="certificate-sign-terms">
                <Input.Checkbox
                  checked={termsChecked}
                  onChange={(event) => setTermsChecked(event.target.checked)}
                  disabled={submitting}
                />
                <span>
                  Li e aceito os{" "}
                  <a href={TERMS_URL} target="_blank" rel="noreferrer">
                    termos de uso
                  </a>
                  .
                </span>
              </label>
            )}

            <div className="certificate-sign-panel-footer">
              <Button
                variant="primary"
                size="lg"
                onClick={handleContinue}
                disabled={!selectedThumbprint || busy || submitting || (needsTerms && !termsChecked)}
                loading={submitting}
              >
                Continuar
              </Button>
            </div>
          </div>

          <div className="certificate-sign-secure">
            <Icon id="icon-lock" size="14" className="me-1" />
            Ambiente seguro Assinafy
          </div>
        </div>
      </div>

      <ModalDeny
        show={!!modalCancel}
        description={modalCancel?.description}
        params={{
          signerAccessCode,
          documentId: signDocument?.id,
          assignmentId: signDocument?.assignment?.id,
        }}
        onClose={() => setModalCancel()}
      />
    </>
  )
}

export default CertificateSignPage
